import {
    VoiShape,
    VoxelBounds,
    VoxelSubRange,
    regionCenterXSpan,
    subRangeIsEmpty,
} from '../services/imageService/gridGeometry';
import { Grid } from '~/engine/data/common/Grid';

/**
 * PET SUV statistics over a voxel window — the pure domain math behind
 * the SUV-threshold tool (SR-WOR-H-27-3). Sibling of {@link petSuv}: the
 * SUVbw conversion factor comes from {@link computeSUVbwFactor}; this
 * module only applies it (SUV = raw × factor) and never re-derives it.
 *
 * Units are explicit throughout: raw values are the image's stored
 * activity (Bq/ml for a BQML series), SUV values are SUVbw in g/ml,
 * volumes are mm³ (the internal domain unit — cm³ is a UI-boundary
 * conversion). The window is a half-open voxel-index range into `grid`
 * (the {@link gridSubRange} shape), so the caller's box snapping and the
 * mask pipeline's snapping agree by construction.
 *
 * SUVpeak (PERCIST max sphere-mean) is deliberately NOT computed here in
 * v1 — it is the one stat that is not O(window)-trivial. When it lands it
 * belongs in this module as another pure function over the same inputs.
 *
 * Every pass takes the region's {@link VoiShape} and honours it. A stat
 * read over the whole window while the mask segments an inscribed
 * ellipsoid would quote a maximum from a corner the structure cannot
 * reach — a threshold relative to a value no segmented voxel holds.
 */

/**
 * Half-open x span of the voxels on row `(y, z)` the region contains,
 * clamped to `window` — the one place the shapes differ, so every pass
 * below is otherwise the same loop.
 *
 * Membership is by voxel CENTRE against the region's EXACT bounds, the
 * same sign the segmentation's metric cut takes there. The window is the
 * snapped superset the loops iterate; counting to its edge instead would
 * quote a maximum from a voxel the structure does not reach.
 */
function rowSpan(
    window: VoxelSubRange,
    bounds: VoxelBounds,
    shape: VoiShape,
    y: number,
    z: number
): { lo: number; hi: number } {
    const span = regionCenterXSpan(bounds, shape, y, z);
    const lo = Math.max(window.minX, span.lo);
    const hi = Math.min(window.maxX, span.hi);
    return { lo, hi: Math.max(lo, hi) };
}

/**
 * Maximum raw voxel value inside the window together with the flat
 * raster-order index (x fastest, then y, then z) of the voxel holding
 * it, or null when the window is empty. Depends on the box only — not
 * on the threshold — so callers cache it per (image, window) and derive
 * the threshold from it.
 *
 * On ties (multiple voxels sharing the maximum) the FIRST voxel in
 * raster order wins — the strict `>` comparison — matching
 * {@link Image3D.findMaxIntensityPosition} and `DoseMatrix.doseMaxPosition`
 * semantics, so every max marker in the application resolves ties the
 * same way. Convert the index to patient-space mm at the caller with
 * {@link Grid.indexToVoxelCenterWorldSpace}.
 */
export function computeWindowRawMaxAt(
    floatPixelData: ArrayLike<number>,
    grid: Grid,
    window: VoxelSubRange,
    bounds: VoxelBounds,
    shape: VoiShape
): { rawMax: number; flatIndex: number } | null {
    if (subRangeIsEmpty(window)) {
        return null;
    }
    const nx = grid.sizeX;
    const sliceSize = nx * grid.sizeY;
    let max = -Infinity;
    let maxIndex = -1;
    for (let z = window.minZ; z < window.maxZ; z++) {
        const zOff = z * sliceSize;
        for (let y = window.minY; y < window.maxY; y++) {
            const rowOff = zOff + y * nx;
            const { lo, hi } = rowSpan(window, bounds, shape, y, z);
            for (let x = lo; x < hi; x++) {
                const v = floatPixelData[rowOff + x];
                if (v > max) {
                    max = v;
                    maxIndex = rowOff + x;
                }
            }
        }
    }
    // An ellipsoid can still select nothing where the window does not:
    // a window whose every row misses it (never reachable from a snapped
    // box, but the caller does not have to prove that here).
    return maxIndex < 0 ? null : { rawMax: max, flatIndex: maxIndex };
}

/**
 * Resolve a %-of-SUVmax threshold to the raw-unit inclusive lower bound
 * the mask pipeline consumes (`voxel ≥ threshold` — the pipeline's
 * documented semantics). The one conversion point: the engine stays
 * unit-agnostic, and the same raw threshold feeds the stats below so the
 * displayed numbers and the segmented mask can never disagree.
 *
 * @param thresholdPct Percentage of the window's raw maximum, in [0, 100].
 * @param windowRawMax Maximum raw value inside the window
 *   ({@link computeWindowRawMaxAt}).
 */
export function suvThresholdRawFromPct(
    thresholdPct: number,
    windowRawMax: number
): number {
    return (thresholdPct / 100) * windowRawMax;
}

/** Threshold-dependent statistics of the raw thresholded mask. */
export interface SuvThresholdStats {
    /** Voxels of the window at or above the raw threshold. */
    segmentedVoxelCount: number;
    /** Mean SUVbw over the segmented voxels (g/ml), null when none. */
    suvMean: number | null;
}

/**
 * One O(window) pass over the raw thresholded mask: `voxel ≥ rawThreshold`
 * inside the window, matching the mask pipeline's inclusive lower bound.
 * These stats describe the RAW mask — before any cleanup stage (fill,
 * smoothing, min-size, keep-largest) — which is why the panel labels them
 * so: an enabled cleanup stage makes the applied structure differ.
 *
 * @param suvFactor SUVbw factor ({@link computeSUVbwFactor}); must be a
 *   usable (finite, positive) factor — the caller gates on
 *   {@link Image3D.isSuvQuantified} before ever computing stats.
 */
export function computeSuvThresholdStats(
    floatPixelData: ArrayLike<number>,
    grid: Grid,
    window: VoxelSubRange,
    bounds: VoxelBounds,
    shape: VoiShape,
    rawThreshold: number,
    suvFactor: number
): SuvThresholdStats {
    const nx = grid.sizeX;
    const sliceSize = nx * grid.sizeY;
    let count = 0;
    let rawSum = 0;
    for (let z = window.minZ; z < window.maxZ; z++) {
        const zOff = z * sliceSize;
        for (let y = window.minY; y < window.maxY; y++) {
            const rowOff = zOff + y * nx;
            const { lo, hi } = rowSpan(window, bounds, shape, y, z);
            for (let x = lo; x < hi; x++) {
                const v = floatPixelData[rowOff + x];
                if (v >= rawThreshold) {
                    count++;
                    rawSum += v;
                }
            }
        }
    }
    const suvMean = count > 0 ? (rawSum / count) * suvFactor : null;
    return { segmentedVoxelCount: count, suvMean };
}
